import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, Plus, UserCheck, UserX, Shield, Cpu } from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CreateCompanyModal, { CompanyFormValues } from '../components/CreateCompanyModal';
import TeamMembersSection from '../components/TeamMembersSection';
import BuyerCompanyMapPanel from '../components/BuyerCompanyMapPanel';
import SectionCompanyMapPanel from '../components/SectionCompanyMapPanel';
import TakaCompanyAssignPanel from '../components/TakaCompanyAssignPanel';
import MachineChipSetupModal from '../components/MachineChipSetupModal';

interface AdminModule {
  id: number;
  name: string;
  displayName: string;
  description: string;
  route: string;
  icon: string;
  category?: string;
  parentModule?: string;
}

interface AdminPermission {
  id: number;
  moduleId: number;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  module: AdminModule;
}

interface Admin {
  id: number;
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  reportEmail?: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'EMPLOYEE';
  isActive?: boolean;
  hasWaterJacket?: boolean;
  hasRapierJacket?: boolean;
  tenantId?: number | null;
  isReseller?: boolean;
  parentResellerId?: number | null;
  machineTenantIds?: number[] | null;
  permissions: AdminPermission[];
}

const CompanyManagement: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'admins' | 'modules'>('modules');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [setupCompany, setSetupCompany] = useState<Admin | null>(null);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    reportEmail: '',
    password: '',
    role: 'ADMIN' as 'ADMIN' | 'EMPLOYEE',
    isReseller: false,
    hasWaterJacket: true,
    hasRapierJacket: false,
    // Companies whose machines this login may view (empty = own workspace only).
    machineTenantIds: [] as number[],
    permissions: [] as any[]
  });

  // Fetch admins (only for Super Admin)
  const { data: adminsData, isLoading: adminsLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: () => apiService.get('/admin/admins'),
    enabled: user?.role === 'SUPERADMIN' || !!user?.isReseller
  });

  // Fetch modules (only for Super Admin)
  const { data: modulesData, isLoading: modulesLoading, error: modulesError } = useQuery({
    queryKey: ['admin-modules'],
    queryFn: () => apiService.get('/admin/modules/all'),
    enabled: user?.role === 'SUPERADMIN' || !!user?.isReseller
  });


  // Existing companies a login can be granted machine access to. Each company is
  // represented by its owner admin (tenantId === id); listed once by display name.
  const companies = useMemo(() => {
    const admins: Admin[] = adminsData?.admins || [];
    return admins
      .filter((a) => a.role !== 'SUPERADMIN' && a.tenantId != null && a.tenantId === a.id)
      .map((a) => ({ tenantId: a.tenantId as number, name: a.companyName || a.name }))
      .sort((x, y) => x.name.localeCompare(y.name));
  }, [adminsData]);

  // Team members may additionally be granted access to the reseller's own company.
  // `companies` above only ever lists companies this reseller separately created
  // (parentResellerId === their id via /admin/admins) — it never includes the
  // reseller's own account, even though a reseller whose tenantId equals their own id
  // runs real company data on that same account (same rule used everywhere else: a
  // company is any ADMIN user with tenantId === id).
  const teamMemberCompanies = useMemo(() => {
    if (!(user?.isReseller && user?.tenantId != null && user.tenantId === user.id)) {
      return companies;
    }
    const own = { tenantId: user.tenantId as number, name: user.companyName || user.name };
    return [...companies, own].sort((x, y) => x.name.localeCompare(y.name));
  }, [companies, user]);

  // Modules this login may grant to a new/edited admin. SUPERADMIN can grant the whole
  // catalog; a reseller can only grant modules they themselves have view access to
  // (mirrors AttendanceApprovals.tsx's grantableModules for the same reason).
  const grantableModules = useMemo(() => {
    const all: AdminModule[] = modulesData?.modules ?? [];
    // company-management is never grantable as a normal module permission — access to it
    // is controlled solely by the isReseller flag (see ProtectedModule.tsx), so granting
    // it here would just show the recipient a page with nothing on it.
    if (!user || user.role === 'SUPERADMIN') return all.filter((m) => m.name !== 'company-management');
    const perms = [...(user.userRole?.permissions ?? []), ...(user.permissions ?? [])];
    const mine = new Map<number, AdminModule>();
    perms.forEach((p: any) => {
      if (p?.canView && p?.module && p.module.name !== 'company-management') mine.set(p.module.id, p.module);
    });
    return Array.from(mine.values());
  }, [user, modulesData]);

  // In SUPERADMIN's view, nest a reseller's companies under that reseller instead of
  // listing everything flat — a company with parentResellerId belongs to its reseller,
  // not directly to SUPERADMIN. Anyone else (a reseller viewing their own already-scoped
  // list) sees a flat list since there's no further nesting to show them.
  const groupedAdminRows = useMemo(() => {
    const admins: Admin[] = adminsData?.admins || [];
    if (user?.role !== 'SUPERADMIN') {
      return admins.map((admin) => ({ admin, depth: 0 }));
    }
    const byParent = new Map<number, Admin[]>();
    const topLevel: Admin[] = [];
    admins.forEach((admin) => {
      if (admin.parentResellerId) {
        if (!byParent.has(admin.parentResellerId)) byParent.set(admin.parentResellerId, []);
        byParent.get(admin.parentResellerId)!.push(admin);
      } else {
        topLevel.push(admin);
      }
    });
    const rows: { admin: Admin; depth: number }[] = [];
    const rendered = new Set<number>();
    topLevel.forEach((admin) => {
      rows.push({ admin, depth: 0 });
      rendered.add(admin.id);
      (byParent.get(admin.id) || []).forEach((child) => {
        rows.push({ admin: child, depth: 1 });
        rendered.add(child.id);
      });
    });
    // Defensive: never silently drop an admin whose parent wasn't in the top-level set.
    admins.forEach((admin) => {
      if (!rendered.has(admin.id)) rows.push({ admin, depth: 0 });
    });
    return rows;
  }, [adminsData, user]);

  const toggleMachineTenant = (tenantId: number) => {
    setNewAdmin((prev) => {
      const has = prev.machineTenantIds.includes(tenantId);
      return {
        ...prev,
        machineTenantIds: has
          ? prev.machineTenantIds.filter((id) => id !== tenantId)
          : [...prev.machineTenantIds, tenantId],
      };
    });
  };


  // Create admin mutation
  const createAdminMutation = useMutation({
    mutationFn: (adminData: any) => apiService.post('/admin/admins', adminData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setShowCreateModal(false);
      setEditingAdmin(null);
      setNewAdmin({ name: '', email: '', phone: '', companyName: '', reportEmail: '', password: '', role: 'ADMIN', isReseller: false, hasWaterJacket: true, hasRapierJacket: false, machineTenantIds: [], permissions: [] });
      toast.success('Admin created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create admin');
    }
  });

  // Update admin mutation
  const updateAdminMutation = useMutation({
    mutationFn: ({ adminId, adminData }: { adminId: number; adminData: any }) =>
      apiService.put(`/admin/admins/${adminId}`, adminData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setShowCreateModal(false);
      setEditingAdmin(null);
      setNewAdmin({ name: '', email: '', phone: '', companyName: '', reportEmail: '', password: '', role: 'ADMIN', isReseller: false, hasWaterJacket: true, hasRapierJacket: false, machineTenantIds: [], permissions: [] });
      toast.success('Admin updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update admin');
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ adminId, isActive }: { adminId: number; isActive: boolean }) => 
      apiService.put(`/admin/admins/${adminId}/status`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      toast.success('Admin status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update admin status');
    }
  });

  // Delete admin mutation
  const deleteAdminMutation = useMutation({
    mutationFn: (adminId: number) => apiService.delete(`/admin/admins/${adminId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      toast.success('Admin deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete admin');
    }
  });

  const handleCreateAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out permissions that don't have canView set to true
    const validPermissions = newAdmin.permissions.filter(p => p.canView);
    
    const adminData = {
      ...newAdmin,
      permissions: validPermissions
    };
    
    createAdminMutation.mutate(adminData);
  };

  const handleUpdateAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingAdmin) {
      // Filter out permissions that don't have canView set to true
      const validPermissions = newAdmin.permissions.filter(p => p.canView);
      
      const adminData = {
        ...newAdmin,
        permissions: validPermissions
      };
      
      updateAdminMutation.mutate({
        adminId: editingAdmin.id,
        adminData
      });
    }
  };

  // Reseller's simplified company form submits here instead of handleCreateAdmin/
  // handleUpdateAdmin — same mutations, a smaller payload (always role ADMIN, no
  // machine/jacket/report-email fields since the simplified form never collects them).
  const handleCreateCompany = (values: CompanyFormValues) => {
    createAdminMutation.mutate({
      name: values.name,
      email: values.email,
      phone: values.phone,
      companyName: values.companyName,
      password: values.password,
      role: 'ADMIN',
      permissions: values.permissions
    });
  };

  const handleUpdateCompany = (values: CompanyFormValues) => {
    if (!editingAdmin) return;
    const adminData: any = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      companyName: values.companyName,
      role: 'ADMIN',
      permissions: values.permissions
    };
    if (values.password) adminData.password = values.password;
    updateAdminMutation.mutate({ adminId: editingAdmin.id, adminData });
  };

  const handleDeleteAdmin = (adminId: number) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      deleteAdminMutation.mutate(adminId);
    }
  };

  const handleEditAdmin = (admin: Admin) => {
    setEditingAdmin(admin);
    setNewAdmin({
      name: admin.name,
      email: admin.email,
      phone: admin.phone || '',
      companyName: admin.companyName || '',
      reportEmail: admin.reportEmail || '',
      password: '',
      role: admin.role === 'SUPERADMIN' ? 'ADMIN' : admin.role,
      hasWaterJacket: admin.hasWaterJacket ?? true,
      hasRapierJacket: admin.hasRapierJacket ?? false,
      isReseller: admin.isReseller ?? false,
      machineTenantIds: admin.machineTenantIds || [],
      permissions: admin.permissions || []
    });
    setShowCreateModal(true);
  };

  return (
    <div className="p-6">
      {/* Admin Management - Only for Super Admin */}
      {(user?.role === 'SUPERADMIN' || user?.isReseller) && (
        <>
          {/* Create Admin Button */}
          <div className="mb-6">
            <button
              onClick={() => {
                setEditingAdmin(null);
                setNewAdmin({ name: '', email: '', phone: '', companyName: '', reportEmail: '', password: '', role: 'ADMIN', isReseller: false, hasWaterJacket: true, hasRapierJacket: false, machineTenantIds: [], permissions: [] });
                setShowCreateModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {user?.role === 'SUPERADMIN' ? 'Create New Admin' : 'Create Company'}
            </button>
          </div>

          {/* Admins List */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Admins</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Report Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Machine Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {adminsLoading ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-4 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      </td>
                    </tr>
                  ) : (
                    groupedAdminRows.map(({ admin, depth }) => (
                      <tr key={admin.id} className={depth > 0 ? 'bg-gray-50' : ''}>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          style={depth > 0 ? { paddingLeft: '2.5rem' } : undefined}
                        >
                          {depth > 0 && <span className="text-gray-300 mr-1">↳</span>}
                          {admin.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {admin.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {admin.reportEmail || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {admin.phone || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {admin.companyName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            admin.role === 'SUPERADMIN'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {admin.role}
                          </span>
                          {admin.isReseller && (
                            <span className="ml-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Reseller
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex gap-1">
                            {admin.hasWaterJacket && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                Water
                              </span>
                            )}
                            {admin.hasRapierJacket && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-800">
                                Rapier
                              </span>
                            )}
                            {!admin.hasWaterJacket && !admin.hasRapierJacket && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-500">
                                None
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            admin.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {admin.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditAdmin(admin)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {/* Company rows only (owner admin: tenantId === id); chips are SUPERADMIN-only. */}
                            {user?.role === 'SUPERADMIN' && admin.role !== 'SUPERADMIN' && admin.tenantId === admin.id && (
                              <button
                                onClick={() => setSetupCompany(admin)}
                                title="Add Machines & Chips"
                                aria-label="Add Machines & Chips"
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <Cpu className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteAdmin(admin.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {user?.isReseller && (
        <TeamMembersSection modules={grantableModules} companies={teamMemberCompanies} />
      )}

      {/* Buyer → Company mapping: which company owns each buyer's yarn data. */}
      {(user?.role === 'SUPERADMIN' || user?.isReseller) && (
        <div className="mt-4">
          <BuyerCompanyMapPanel />
        </div>
      )}

      {/* Section → Company mapping: which company sees each Yarn Dashboard section. */}
      {(user?.role === 'SUPERADMIN' || user?.isReseller) && (
        <div className="mt-4">
          <SectionCompanyMapPanel />
        </div>
      )}

      {/* Taka Inventory one-time re-assign to a company. */}
      {(user?.role === 'SUPERADMIN' || user?.isReseller) && (
        <div className="mt-4">
          <TakaCompanyAssignPanel />
        </div>
      )}

      {setupCompany && setupCompany.tenantId != null && (
        <MachineChipSetupModal
          tenantId={setupCompany.tenantId}
          companyName={setupCompany.companyName || setupCompany.name}
          onClose={() => setSetupCompany(null)}
        />
      )}

      {/* Create/Edit Admin Modal — SUPERADMIN's full form, unchanged */}
      {showCreateModal && user?.role === 'SUPERADMIN' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingAdmin ? 'Edit Admin' : 'Create New Admin'}
            </h2>
            <form onSubmit={editingAdmin ? handleUpdateAdmin : handleCreateAdmin}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Email
                </label>
                <input
                  type="email"
                  value={newAdmin.reportEmail}
                  onChange={(e) => setNewAdmin({ ...newAdmin, reportEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="reports@company.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Daily reports are sent here. Leave blank to use the login email.
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={newAdmin.phone}
                  onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={newAdmin.companyName}
                  onChange={(e) => setNewAdmin({ ...newAdmin, companyName: e.target.value })}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter company name"
                />
              </div>

              {/* Machine Data Companies — which companies' machines this login may view */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Machine Data Companies
                </label>
                {companies.length === 0 ? (
                  <p className="text-sm text-gray-500">No other companies available yet.</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                    {companies.map((c) => (
                      <label
                        key={c.tenantId}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={newAdmin.machineTenantIds.includes(c.tenantId)}
                          onChange={() => toggleMachineTenant(c.tenantId)}
                        />
                        <span className="text-sm text-gray-800">{c.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Select one or more companies. This login can switch between them on the
                  Machines page and see each company's machines. Leave empty for a login
                  with its own workspace only.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!editingAdmin}
                  placeholder={editingAdmin ? "Leave blank to keep current password" : ""}
                />
              </div>

              {/* Machine Access Toggles */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Machine Access
                </label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Water Jacket</span>
                    <button
                      type="button"
                      onClick={() => setNewAdmin({ ...newAdmin, hasWaterJacket: !newAdmin.hasWaterJacket })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        newAdmin.hasWaterJacket ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          newAdmin.hasWaterJacket ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Rapier Jacket</span>
                    <button
                      type="button"
                      onClick={() => setNewAdmin({ ...newAdmin, hasRapierJacket: !newAdmin.hasRapierJacket })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        newAdmin.hasRapierJacket ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          newAdmin.hasRapierJacket ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Reseller Access Toggle — only SUPERADMIN may grant this */}
              {user?.role === 'SUPERADMIN' && (
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Reseller Access</span>
                      <p className="text-xs text-gray-500">Lets this company owner create and manage their own companies.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewAdmin({ ...newAdmin, isReseller: !newAdmin.isReseller })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        newAdmin.isReseller ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          newAdmin.isReseller ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}

              {/* Module Access Selection */}
              {modulesLoading ? (
                <div className="mb-6">
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Loading modules...</p>
                  </div>
                </div>
              ) : modulesError ? (
                <div className="mb-6">
                  <div className="text-center py-4">
                    <p className="text-sm text-red-500">Error loading modules: {modulesError.message}</p>
                  </div>
                </div>
              ) : modulesData && modulesData.modules ? (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Module Access
                  </label>
                  {/* No inner scroll box — the modal itself scrolls, so the full list is visible. */}
                  <div className="space-y-4">
                    {/* Main Three Modules */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Main Modules</h4>
                      <div className="space-y-2">
                        {grantableModules.filter((module: any) => module.category === 'main').map((module: AdminModule) => (
                      <div key={module.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-gray-900">{module.displayName}</h4>
                            <p className="text-sm text-gray-500">{module.description}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={newAdmin.permissions.some((p: any) => p.moduleId === module.id && p.canView)}
                              onChange={(e) => {
                                const newPermissions = newAdmin.permissions.filter((p: any) => p.moduleId !== module.id);
                                if (e.target.checked) {
                                  newPermissions.push({
                                    moduleId: module.id,
                                    canView: true,
                                    canCreate: false,
                                    canUpdate: false,
                                    canDelete: false
                                  });
                                }
                                setNewAdmin({ ...newAdmin, permissions: newPermissions });
                              }}
                              className="rounded"
                            />
                            <span className="text-sm text-gray-700">Access</span>
                          </div>
                        </div>
                        {newAdmin.permissions.some((p: any) => p.moduleId === module.id && p.canView) && (
                          <div className="grid grid-cols-3 gap-2 mt-2">
                            <div className="flex items-center space-x-1">
                              <input
                                type="checkbox"
                                checked={newAdmin.permissions.find((p: any) => p.moduleId === module.id)?.canCreate || false}
                                onChange={(e) => {
                                  const newPermissions = newAdmin.permissions.map((p: any) => 
                                    p.moduleId === module.id ? { ...p, canCreate: e.target.checked } : p
                                  );
                                  setNewAdmin({ ...newAdmin, permissions: newPermissions });
                                }}
                                className="rounded"
                              />
                              <span className="text-xs text-gray-600">Create</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <input
                                type="checkbox"
                                checked={newAdmin.permissions.find((p: any) => p.moduleId === module.id)?.canUpdate || false}
                                onChange={(e) => {
                                  const newPermissions = newAdmin.permissions.map((p: any) => 
                                    p.moduleId === module.id ? { ...p, canUpdate: e.target.checked } : p
                                  );
                                  setNewAdmin({ ...newAdmin, permissions: newPermissions });
                                }}
                                className="rounded"
                              />
                              <span className="text-xs text-gray-600">Update</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <input
                                type="checkbox"
                                checked={newAdmin.permissions.find((p: any) => p.moduleId === module.id)?.canDelete || false}
                                onChange={(e) => {
                                  const newPermissions = newAdmin.permissions.map((p: any) => 
                                    p.moduleId === module.id ? { ...p, canDelete: e.target.checked } : p
                                  );
                                  setNewAdmin({ ...newAdmin, permissions: newPermissions });
                                }}
                                className="rounded"
                              />
                              <span className="text-xs text-gray-600">Delete</span>
                            </div>
                          </div>
                        )}
                      </div>
                        ))}
                      </div>
                    </div>

                    {/* Textile Modules — always shown. They used to wait for 'company-management'
                        to be ticked, but grantableModules filters that module out, so they never appeared. */}
                    {grantableModules.some((module: any) => module.category !== 'main') && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Textile Modules</h4>
                        <div className="space-y-2">
                          {grantableModules.filter((module: any) => module.category !== 'main').map((module: AdminModule) => (
                            <div key={module.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <h4 className="font-medium text-gray-900">{module.displayName}</h4>
                                  <p className="text-sm text-gray-500">{module.description}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={newAdmin.permissions.some((p: any) => p.moduleId === module.id && p.canView)}
                                    onChange={(e) => {
                                      const newPermissions = newAdmin.permissions.filter((p: any) => p.moduleId !== module.id);
                                      if (e.target.checked) {
                                        newPermissions.push({
                                          moduleId: module.id,
                                          canView: true,
                                          canCreate: false,
                                          canUpdate: false,
                                          canDelete: false
                                        });
                                      }
                                      setNewAdmin({ ...newAdmin, permissions: newPermissions });
                                    }}
                                    className="rounded"
                                  />
                                  <span className="text-sm text-gray-700">Access</span>
                                </div>
                              </div>
                              {newAdmin.permissions.some((p: any) => p.moduleId === module.id && p.canView) && (
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={newAdmin.permissions.find((p: any) => p.moduleId === module.id)?.canCreate || false}
                                      onChange={(e) => {
                                        const newPermissions = newAdmin.permissions.map((p: any) => 
                                          p.moduleId === module.id ? { ...p, canCreate: e.target.checked } : p
                                        );
                                        setNewAdmin({ ...newAdmin, permissions: newPermissions });
                                      }}
                                      className="rounded"
                                    />
                                    <span className="text-xs text-gray-600">Create</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={newAdmin.permissions.find((p: any) => p.moduleId === module.id)?.canUpdate || false}
                                      onChange={(e) => {
                                        const newPermissions = newAdmin.permissions.map((p: any) => 
                                          p.moduleId === module.id ? { ...p, canUpdate: e.target.checked } : p
                                        );
                                        setNewAdmin({ ...newAdmin, permissions: newPermissions });
                                      }}
                                      className="rounded"
                                    />
                                    <span className="text-xs text-gray-600">Update</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={newAdmin.permissions.find((p: any) => p.moduleId === module.id)?.canDelete || false}
                                      onChange={(e) => {
                                        const newPermissions = newAdmin.permissions.map((p: any) => 
                                          p.moduleId === module.id ? { ...p, canDelete: e.target.checked } : p
                                        );
                                        setNewAdmin({ ...newAdmin, permissions: newPermissions });
                                      }}
                                      className="rounded"
                                    />
                                    <span className="text-xs text-gray-600">Delete</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">No modules available</p>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value as 'ADMIN' | 'EMPLOYEE' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ADMIN">Admin</option>
                  <option value="EMPLOYEE">Employee</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingAdmin(null);
                      setNewAdmin({ name: '', email: '', phone: '', companyName: '', reportEmail: '', password: '', role: 'ADMIN', isReseller: false, hasWaterJacket: true, hasRapierJacket: false, machineTenantIds: [], permissions: [] });
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                <button
                  type="submit"
                  disabled={createAdminMutation.isPending || updateAdminMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createAdminMutation.isPending || updateAdminMutation.isPending 
                    ? 'Saving...' 
                    : editingAdmin ? 'Update Admin' : 'Create Admin'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Company Modal — resellers' simplified form */}
      {showCreateModal && user?.role !== 'SUPERADMIN' && (
        <CreateCompanyModal
          modules={grantableModules}
          initial={
            editingAdmin
              ? {
                  name: editingAdmin.name,
                  email: editingAdmin.email,
                  phone: editingAdmin.phone || '',
                  companyName: editingAdmin.companyName || '',
                  permissions: editingAdmin.permissions.map((p) => ({ moduleId: p.moduleId }))
                }
              : null
          }
          onClose={() => {
            setShowCreateModal(false);
            setEditingAdmin(null);
          }}
          onSubmit={editingAdmin ? handleUpdateCompany : handleCreateCompany}
          submitting={createAdminMutation.isPending || updateAdminMutation.isPending}
        />
      )}

    </div>
  );
};


export default CompanyManagement;
